import { Field, Formik, FormikHelpers as FormikActions } from "formik";
import React from "react";
import {
  Form,
  FormRow,
  FormColumn,
  InputField,
  SelectField,
  FormControls,
  FormTitle,
  IntentText,
} from "~/components/Form";
import { IModalComponent, IModalComponentClose } from "~/components/Modal";
import { Button } from "~/components/Button";
import { Intent } from "~/lib/intent";
import { ILine, getLineSchema } from "../models/Line";
import { SelectDomainField } from "../components/SelectDomain";
import { SelectSourceField } from "~/modules/Bidders/components/SelectSource";

interface ILineFormProps {
  line?: ILine;
  agencyId: number;
  dspConnectionType?: string;
  onSubmit?: (line: ILine, formikActions: FormikActions<any>, close: IModalComponentClose) => void;
}

function buildScope(scope: string, scopeValue: string): string {
  switch (scope) {
    case "public":
      return "public";
    case "seller":
      return `seller:${scopeValue}`;
    case "domain":
      return `domain:${scopeValue}`;
    default:
      return scope;
  }
}

type Option = { label: string; value: string };

function sellerTypeOptionsFor(dspConstrained: boolean): Option[] {
  return dspConstrained
    ? [{ label: "DIRECT", value: "DIRECT" }]
    : [{ label: "RESELLER", value: "RESELLER" }, { label: "EITHER", value: "EITHER" }];
}

function scopeOptionsFor(dspConstrained: boolean, sellerType: string): Option[] {
  const allowPublic = !dspConstrained && sellerType !== "EITHER";
  return [
    ...(allowPublic ? [{ label: "Public", value: "public" }] : []),
    { label: "Seller", value: "seller" },
    { label: "Domain", value: "domain" },
  ];
}

function withCurrentValue(options: Option[], current: string): Option[] {
  if (!current || options.some((o) => o.value === current)) {
    return options;
  }
  return [...options, { label: `${current} (not allowed)`, value: current }];
}

export const LineForm: IModalComponent<ILineFormProps> = (props) => {
  const { line, agencyId, dspConnectionType } = props;

  // Agencies wired to a DSP connection type may only have DIRECT lines scoped to a seller or a domain.
  const dspConstrained = Boolean(dspConnectionType);

  const normalizedId =
    line && typeof line.id === "object" ? (line.id as any).value : line?.id;

  let scope = dspConstrained ? "seller" : "public";
  let scopeValue: number | string = "";

  if (line?.scope) {
    if (line.scope.startsWith("seller:")) {
      scope = "seller";
      scopeValue = line.scope.split(":")[1] ? Number(line.scope.split(":")[1]) : "";
    } else if (line.scope.startsWith("domain:")) {
      scope = "domain";
      scopeValue = line.scope.split(":")[1] || "";
    } else if (line.scope === "public") {
      scope = "public";
      scopeValue = "";
    }
  }

  const defaultSellerType = line?.sellerType ?? sellerTypeOptionsFor(dspConstrained)[0].value;

  const initialSellerTypeInvalid =
    Boolean(line) && !sellerTypeOptionsFor(dspConstrained).some((o) => o.value === defaultSellerType);
  const initialScopeInvalid =
    Boolean(line) && scope === "public" && (dspConstrained || defaultSellerType === "EITHER");

  return (
    <Formik
      initialValues={{
        id: normalizedId ?? undefined,
        domainId: line?.domainId ?? null,
        externalSellerId: line?.externalSellerId || "",
        resellerDomain: line?.resellerDomain || "",
        sellerType: defaultSellerType,
        scope,
        scopeValue,
        status: line?.status || "valid",
      }}
      validationSchema={getLineSchema(dspConstrained)}
      validateOnMount
      initialTouched={{
        ...(initialSellerTypeInvalid ? { sellerType: true } : {}),
        ...(initialScopeInvalid ? { scope: true } : {}),
      }}
      onSubmit={(values, actions) => {
        const payload = {
          id: values.id,
          agencyId: agencyId,
          domainId: Number(values.domainId),
          externalSellerId: values.externalSellerId,
          resellerDomain: values.resellerDomain,
          sellerType: values.sellerType,
          scope: buildScope(values.scope, String(values.scopeValue)),
          status: values.status,
        };

        props.onSubmit?.(payload as ILine, actions, props.close);
      }}
    >
      {(formik) => (
        <Form onSubmit={formik.handleSubmit}>
          <FormTitle>{line ? "Edit Line" : "Create Line"}</FormTitle>

          {formik.status && (
            <IntentText intent={Intent.DANGER}>{formik.status}</IntentText>
          )}

          {dspConstrained && (
            <IntentText intent={Intent.PRIMARY}>
              This agency has a DSP Connection Type, so only DIRECT lines scoped to a seller or a domain are allowed.
            </IntentText>
          )}

          <FormRow>
            <FormColumn>
              <Field
                name="domainId"
                label="Ad System Domain"
                component={SelectDomainField}
                options={{
                  agencyId,
                  placeholder: "Select domain",
                  disabled: formik.isSubmitting,
                  domainName: line?.domain,
                  ownDomainsOnly: dspConstrained,
                }}
              />

              <Field
                name="externalSellerId"
                label="Advertiser ID"
                component={InputField}
                options={{ disabled: formik.isSubmitting }}
              />

              <Field
                name="sellerType"
                label="Seller Type"
                component={SelectField}
                options={{
                  placeholder: "Select type",
                  items: withCurrentValue(sellerTypeOptionsFor(dspConstrained), formik.values.sellerType),
                  disabled: formik.isSubmitting,
                }}
              />
            </FormColumn>

            <FormColumn>

              <Field
                name="scope"
                label="Scope"
                component={SelectField}
                options={{
                  placeholder: "Select scope",
                  items: withCurrentValue(
                    scopeOptionsFor(dspConstrained, formik.values.sellerType),
                    formik.values.scope
                  ),
                  disabled: formik.isSubmitting,
                }}
              />

              {formik.values.scope === "seller" && (
                <Field
                  name="scopeValue"
                  label="Seller"
                  component={SelectSourceField}
                  options={{
                    placeholder: "Select seller",
                    disabled: formik.isSubmitting,
                    searchable: true,
                  }}
                />
              )}

              {formik.values.scope === "domain" && (
                <Field
                  name="scopeValue"
                  label="Qualified domain"
                  component={InputField}
                  options={{ disabled: formik.isSubmitting }}
                />
              )}

            </FormColumn>
          </FormRow>

          <FormControls>
            <Button onClick={props.close} disabled={formik.isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              intent={Intent.PRIMARY}
              disabled={
                formik.isSubmitting ||
                Boolean(formik.errors.sellerType) ||
                Boolean(formik.errors.scope)
              }
              rightIcon={
                formik.isSubmitting && <i className="fa fa-spinner fa-spin" />
              }
            >
              Save
            </Button>
          </FormControls>
        </Form>
      )}
    </Formik>
  );
};
